/*!
 * EndlessPianoEngine v2 — generative ambient piano with a CLASSICAL composer.
 *
 * v2 (2026-07-10, after Farid's review of v1: "не слышу мелодий приятных"):
 * v1 fixed correctness (0% out-of-key) but shuffled short phrase cells over a
 * metronomic accompaniment — correct, not musical. v2 composes the way piano
 * music is actually formed:
 *
 *  - PERIOD FORM: every 8 bars is an antecedent/consequent pair — the answer
 *    reuses the question's rhythm and opening tone but resolves to the tonic.
 *    Phrases end in real cadences (authentic V→I, plagal, aeolian VII→i,
 *    half cadences mid-period, ~12% deceptive V→vi as the "pleasant surprise").
 *  - EVERY NOTE HAS A ROLE: strong beats carry chord tones; weak beats carry
 *    stepwise passing/neighbor tones; suspensions are prepared, held over the
 *    barline and resolved DOWN by step; the phrase climax may take an
 *    appoggiatura. After a leap the line steps back (counterpoint recovery).
 *  - HARMONIC COLOR: add9 / maj7 / m7 / sus4→3 voicings, chosen per mood;
 *    V in minor raises its third (real leading tone) — natural minor
 *    elsewhere; bass moves through inversions for stepwise motion.
 *  - TEXTURES & FORM: flowing 8th arpeggios (Einaudi), bass+chord (Satie),
 *    chorale pads, melody-in-thirds, solo line — arranged in long sections
 *    (intro → A → A' → bridge → B → A'' → breath) per mood.
 *  - PERFORMANCE: sustain-pedal realism (left hand rings until the chord
 *    changes), melody leads the accompaniment by ~15 ms, LH ≈7 dB under RH,
 *    phrase-shaped dynamics, ritenuto into cadences.
 *  - BACKGROUND-PROOF playback (user-reported: tab switch killed the music):
 *    the scheduler ticks from a dedicated Worker via window.__audioBackground
 *    (worker timers are not visibility-throttled) and the look-ahead grows to
 *    90 s when the tab is hidden — enough to survive even once-per-minute
 *    main-thread throttling if the worker is unavailable.
 *
 * The mood data (progressions, rhythm vocabulary, arpeggio figures, section
 * plans) is authored, compact (~KBs) and lives in this lazily-loaded file;
 * if the base grows past that, move MOODS to /data JSON fetched on demand.
 *
 * Samples: Salamander Grand Piano (Alexander Holm, CC BY 3.0), 21 anchors.
 * ES5-safe. Node-importable for the composer audit (tests/scratch).
 */
(function (root) {
'use strict';

/* ============================== */
/* PRNG                           */
/* ============================== */
function mulberry32(seed) {
    return function () {
        seed |= 0; seed = seed + 0x6D2B79F5 | 0;
        var t = Math.imul(seed ^ seed >>> 15, 1 | seed);
        t = t + Math.imul(t ^ t >>> 7, 61 | t) ^ t;
        return ((t ^ t >>> 14) >>> 0) / 4294967296;
    };
}
function hashStr(s) {
    var h = 0;
    for (var i = 0; i < s.length; i++) h = ((h << 5) - h) + s.charCodeAt(i) | 0;
    return h;
}

/* ============================== */
/* THEORY CORE                    */
/* ============================== */
function pcOf(m) { return ((m % 12) + 12) % 12; }
function foldToRange(midi, lo, hi) {
    while (midi < lo) midi += 12;
    while (midi > hi) midi -= 12;
    return midi;
}
/* degree (any int) → semitones from scale root, octave-folded over a scale array */
function degToSemisIn(notes, deg) {
    var len = notes.length;
    var oct = Math.floor(deg / len);
    var idx = ((deg % len) + len) % len;
    return notes[idx] + oct * 12;
}

/*
 * Chord spec: { d: scale degree of root, ext: null|'7', add9: bool, sus4: bool,
 *               raise3: bool (leading tone: V in minor) }
 * Core tones (root/3rd/5th[/7th]) are what the melody treats as chord tones;
 * add9 is a voicing color only.
 */
function chordCore(scaleNotes, spec) {
    var r = degToSemisIn(scaleNotes, spec.d);
    var third = degToSemisIn(scaleNotes, spec.d + 2);
    if (spec.sus4) third = degToSemisIn(scaleNotes, spec.d + 3);
    if (spec.raise3) third = third + 1;
    var out = [r, third, degToSemisIn(scaleNotes, spec.d + 4)];
    if (spec.ext === '7') out.push(degToSemisIn(scaleNotes, spec.d + 6));
    return out;
}
function chordColor9(scaleNotes, spec) { return degToSemisIn(scaleNotes, spec.d + 8); }

/* kept exported for the etalon ratchet test + plain-triad uses */
function diatonicChord(scale, rootDeg) {
    return [degToSemisIn(scale.notes, rootDeg), degToSemisIn(scale.notes, rootDeg + 2), degToSemisIn(scale.notes, rootDeg + 4)];
}

/* ============================== */
/* AUTHORED MOOD DATA             */
/* ============================== */
/* chord shorthand helper */
function C(d, o) {
    o = o || {};
    return { d: d, ext: o.ext || null, add9: !!o.add9, sus4: !!o.sus4, raise3: !!o.raise3 };
}

/*
 * Progressions: 8 chords = 8 bars (antecedent 0-3 ends in a HALF cadence
 * region, consequent 4-7 ends in the labeled final cadence). 'auth' cadences
 * may mutate into a deceptive resolution (V→vi/VI) with a small probability.
 */
var MOODS = {

    /* ─── CALM — C major, Einaudi language ─── */
    calm: {
        root: 60,
        scale: [0, 2, 4, 5, 7, 9, 11],
        melodyCenter: 72,
        tonicDeg: 0, domDeg: 4, subDeg: 5,
        lhBase: 0.30, rhBase: 0.62,
        progs: [
            { name: 'nuvole',   bars: [C(5,{add9:1}), C(3), C(0,{add9:1}), C(4)],      cons: [C(5,{add9:1}), C(3), C(4,{sus4:1}), C(0,{add9:1})], cad: 'auth' },
            { name: 'mattina',  bars: [C(0,{add9:1}), C(5), C(3,{ext:'7'}), C(4)],     cons: [C(0,{add9:1}), C(5), C(1,{ext:'7'}), C(0)],          cad: 'auth' },
            { name: 'canon',    bars: [C(0), C(4), C(5), C(2)],                        cons: [C(3), C(0), C(3), C(0)],                              cad: 'plagal' },
            { name: 'pastoral', bars: [C(0,{add9:1}), C(3,{ext:'7'}), C(0), C(4,{sus4:1})], cons: [C(0,{add9:1}), C(3), C(4), C(0,{add9:1})],       cad: 'auth' },
            { name: 'meadow',   bars: [C(0,{add9:1}), C(5), C(3), C(4)],               cons: [C(0,{add9:1}), C(5), C(4,{sus4:1}), C(0,{add9:1})],   cad: 'auth' }
        ],
        progsAlt: [
            { name: 'lift',     bars: [C(3,{ext:'7'}), C(4), C(0,{add9:1}), C(0)],     cons: [C(3,{ext:'7'}), C(4,{sus4:1}), C(4), C(0,{add9:1})], cad: 'auth' },
            { name: 'circle',   bars: [C(0), C(2,{ext:'7'}), C(5,{ext:'7'}), C(1,{ext:'7'})], cons: [C(3,{ext:'7'}), C(1,{ext:'7'}), C(4), C(0)],   cad: 'auth' },
            { name: 'brook',    bars: [C(0,{add9:1}), C(1,{ext:'7'}), C(4), C(0)],     cons: [C(3), C(0), C(3), C(0,{add9:1})],                     cad: 'plagal' }
        ],
        rhy: {
            open: [[2,1,1],[1,1,2],[1.5,0.5,2],[1,0.5,0.5,2],[2,2],[0.5,0.5,1,2]],
            mid:  [[1,1,1,1],[1.5,0.5,1,1],[1,0.5,0.5,1,1],[2,1,0.5,0.5],[1,1,1.5,0.5],[0.5,0.5,1,0.5,0.5,1]],
            cad:  [[2,2],[1,3],[1,1,2],[1.5,0.5,2]],
            sparse: [[4],[2,2],[3,-1],[2,1,-1]]
        },
        figs: {
            arp8: [
                [{t:'B',o:0,b:0,d:.5},{t:2,o:0,b:.5,d:.5},{t:0,o:1,b:1,d:.5},{t:1,o:1,b:1.5,d:.5},{t:2,o:1,b:2,d:.5},{t:1,o:1,b:2.5,d:.5},{t:0,o:1,b:3,d:.5},{t:2,o:0,b:3.5,d:.5}],
                [{t:'B',o:0,b:0,d:.5},{t:2,o:0,b:.5,d:.5},{t:0,o:1,b:1,d:.5},{t:2,o:0,b:1.5,d:.5},{t:1,o:1,b:2,d:.5},{t:2,o:0,b:2.5,d:.5},{t:0,o:1,b:3,d:.5},{t:2,o:0,b:3.5,d:.5}],
                [{t:'B',o:0,b:0,d:1},{t:2,o:0,b:1,d:.5},{t:0,o:1,b:1.5,d:.5},{t:1,o:1,b:2,d:1},{t:0,o:1,b:3,d:.5},{t:2,o:0,b:3.5,d:.5}]
            ],
            half: [
                [{t:'B',o:0,b:0,d:2},{t:2,o:0,b:2,d:1},{t:0,o:1,b:3,d:1}],
                [{t:'B',o:0,b:0,d:2},{t:1,o:1,b:2,d:2}]
            ]
        },
        sections: [
            { name: 'intro',  tex: 'chorale', dyn: 0.40, bars: 4,  melody: false },
            { name: 'A',      tex: 'arp8',    dyn: 0.55, bars: 16, melody: true },
            { name: "A'",     tex: 'arp8',    dyn: 0.70, bars: 16, melody: true, dyads: true },
            { name: 'bridge', tex: 'half',    dyn: 0.45, bars: 8,  melody: true },
            { name: 'B',      tex: 'arp8',    dyn: 0.62, bars: 16, melody: true, alt: true },
            { name: "A''",    tex: 'arp8',    dyn: 0.75, bars: 16, melody: true, dyads: true },
            { name: 'breath', tex: 'chorale', dyn: 0.38, bars: 4,  melody: false }
        ],
        dev: { susp: 0.45, app: 0.40, deceptive: 0.12 }
    },

    /* ─── DREAMY — Db major, maj7 planing, Debussy/Satie language ─── */
    dreamy: {
        root: 61,
        scale: [0, 2, 4, 5, 7, 9, 11],
        melodyCenter: 73,
        tonicDeg: 0, domDeg: 4, subDeg: 5,
        lhBase: 0.27, rhBase: 0.56,
        progs: [
            { name: 'gymno',    bars: [C(0,{ext:'7'}), C(3,{ext:'7'}), C(0,{ext:'7'}), C(4,{sus4:1})], cons: [C(0,{ext:'7'}), C(3,{ext:'7'}), C(4), C(0,{ext:'7'})], cad: 'auth' },
            { name: 'plane',    bars: [C(0,{ext:'7'}), C(1,{ext:'7'}), C(2,{ext:'7'}), C(1,{ext:'7'})], cons: [C(3,{ext:'7'}), C(2,{ext:'7'}), C(1,{ext:'7'}), C(0,{ext:'7'})], cad: 'plagal' },
            { name: 'lull',     bars: [C(0,{ext:'7'}), C(5,{ext:'7'}), C(3,{ext:'7'}), C(4,{sus4:1})], cons: [C(0,{ext:'7'}), C(5,{ext:'7'}), C(4), C(0,{ext:'7'})], cad: 'auth' },
            { name: 'mist',     bars: [C(0,{ext:'7'}), C(2,{ext:'7'}), C(3,{ext:'7'}), C(4,{sus4:1})], cons: [C(0,{ext:'7'}), C(2,{ext:'7'}), C(4), C(0,{ext:'7'})], cad: 'auth' }
        ],
        progsAlt: [
            { name: 'shimmer',  bars: [C(3,{ext:'7'}), C(0,{ext:'7'}), C(3,{ext:'7'}), C(4)],          cons: [C(5,{ext:'7'}), C(3,{ext:'7'}), C(4,{sus4:1}), C(0,{ext:'7'})], cad: 'auth' },
            { name: 'drift',    bars: [C(5,{ext:'7'}), C(4,{ext:'7'}), C(3,{ext:'7'}), C(2,{ext:'7'})], cons: [C(1,{ext:'7'}), C(2,{ext:'7'}), C(4), C(0,{ext:'7'})],          cad: 'auth' }
        ],
        rhy: {
            open: [[3,1],[2,1.5,0.5],[2,2],[1.5,1.5,1],[4]],
            mid:  [[1,2,1],[1.5,0.5,1.5,0.5],[0.5,0.5,2,1],[2,0.5,0.5,1],[1,1,2],[2,1,1]],
            cad:  [[3,1],[2,2],[1,3],[4]],
            sparse: [[4],[3,-1],[2,-2],[2,2]]
        },
        figs: {
            arp8: [
                [{t:'B',o:0,b:0,d:.5},{t:2,o:0,b:.5,d:.5},{t:3,o:0,b:1,d:.5},{t:0,o:1,b:1.5,d:.5},{t:1,o:1,b:2,d:.5},{t:0,o:1,b:2.5,d:.5},{t:3,o:0,b:3,d:.5},{t:2,o:0,b:3.5,d:.5}],
                [{t:'B',o:0,b:0,d:1},{t:2,o:0,b:1,d:.5},{t:3,o:0,b:1.5,d:.5},{t:0,o:1,b:2,d:.5},{t:3,o:0,b:2.5,d:.5},{t:1,o:1,b:3,d:1}]
            ],
            half: [
                [{t:'B',o:0,b:0,d:2},{t:2,o:0,b:2,d:1},{t:3,o:0,b:3,d:1}],
                [{t:'B',o:0,b:0,d:2},{t:3,o:0,b:2,d:2}]
            ]
        },
        sections: [
            { name: 'intro',  tex: 'chorale', dyn: 0.35, bars: 4,  melody: false },
            { name: 'A',      tex: 'arp8',    dyn: 0.50, bars: 16, melody: true },
            { name: "A'",     tex: 'arp8',    dyn: 0.62, bars: 16, melody: true, dyads: true },
            { name: 'bridge', tex: 'half',    dyn: 0.42, bars: 8,  melody: true },
            { name: 'B',      tex: 'arp8',    dyn: 0.56, bars: 16, melody: true, alt: true },
            { name: "A''",    tex: 'arp8',    dyn: 0.66, bars: 16, melody: true, dyads: true },
            { name: 'breath', tex: 'chorale', dyn: 0.34, bars: 4,  melody: false }
        ],
        dev: { susp: 0.55, app: 0.35, deceptive: 0.10 }
    },

    /* ─── MELANCHOLIC — A minor, nocturne language, real leading tone on V ─── */
    melancholic: {
        root: 57,
        scale: [0, 2, 3, 5, 7, 8, 10],
        melodyCenter: 69,
        tonicDeg: 0, domDeg: 4, subDeg: 5,
        lhBase: 0.30, rhBase: 0.60,
        progs: [
            { name: 'nocturne', bars: [C(0,{add9:1}), C(3,{ext:'7'}), C(5,{ext:'7'}), C(4,{raise3:1})], cons: [C(0,{add9:1}), C(3,{ext:'7'}), C(4,{raise3:1}), C(0)], cad: 'auth' },
            { name: 'lament',   bars: [C(0), C(6), C(5), C(4,{raise3:1})],                              cons: [C(0), C(3), C(4,{raise3:1}), C(0,{add9:1})],           cad: 'auth' },
            { name: 'aeolian',  bars: [C(0,{add9:1}), C(6), C(5,{ext:'7'}), C(6)],                      cons: [C(0,{add9:1}), C(5), C(6), C(0)],                       cad: 'aeol' },
            { name: 'elegy',    bars: [C(0,{add9:1}), C(5,{ext:'7'}), C(3,{ext:'7'}), C(4,{raise3:1})], cons: [C(0,{add9:1}), C(6), C(4,{raise3:1}), C(0)],           cad: 'auth' }
        ],
        progsAlt: [
            { name: 'hope',     bars: [C(5,{ext:'7'}), C(2), C(6), C(0)],                               cons: [C(5,{ext:'7'}), C(3,{ext:'7'}), C(4,{raise3:1}), C(0)], cad: 'auth' },
            { name: 'veil',     bars: [C(0), C(3), C(6), C(5,{ext:'7'})],                               cons: [C(0,{add9:1}), C(6), C(5), C(0)],                       cad: 'aeol' }
        ],
        rhy: {
            open: [[2,1,1],[3,1],[1,1,2],[2,2],[2,1,-1]],
            mid:  [[2,1,1],[1,1,-1,1],[1.5,0.5,2],[3,1],[1,-0.5,0.5,2],[2,0.5,0.5,1],[1,1,1,1]],
            cad:  [[2,2],[3,1],[1,3],[4]],
            sparse: [[4],[2,2],[3,-1],[1,1,-2]]
        },
        figs: {
            arp8: [
                [{t:'B',o:0,b:0,d:.5},{t:2,o:0,b:.5,d:.5},{t:0,o:1,b:1,d:.5},{t:2,o:0,b:1.5,d:.5},{t:1,o:1,b:2,d:.5},{t:2,o:0,b:2.5,d:.5},{t:0,o:1,b:3,d:.5},{t:2,o:0,b:3.5,d:.5}],
                [{t:'B',o:0,b:0,d:1},{t:2,o:0,b:1,d:.5},{t:0,o:1,b:1.5,d:.5},{t:2,o:0,b:2,d:.5},{t:1,o:1,b:2.5,d:.5},{t:0,o:1,b:3,d:1}]
            ],
            half: [
                [{t:'B',o:0,b:0,d:2},{t:2,o:0,b:2,d:2}],
                [{t:'B',o:0,b:0,d:2},{t:2,o:0,b:2,d:1},{t:1,o:1,b:3,d:1}]
            ],
            umpah: [
                [{t:'B',o:0,b:0,d:4},{t:1,o:1,b:1,d:1,dy:2},{t:1,o:1,b:2,d:1,dy:2},{t:1,o:1,b:3,d:1,dy:2}]
            ]
        },
        sections: [
            { name: 'intro',  tex: 'line',    dyn: 0.42, bars: 4,  melody: true },
            { name: 'A',      tex: 'umpah',   dyn: 0.52, bars: 16, melody: true },
            { name: "A'",     tex: 'arp8',    dyn: 0.62, bars: 16, melody: true },
            { name: 'bridge', tex: 'chorale', dyn: 0.40, bars: 8,  melody: false },
            { name: 'B',      tex: 'arp8',    dyn: 0.58, bars: 16, melody: true, alt: true },
            { name: "A''",    tex: 'arp8',    dyn: 0.68, bars: 16, melody: true, dyads: true },
            { name: 'breath', tex: 'chorale', dyn: 0.36, bars: 4,  melody: false }
        ],
        dev: { susp: 0.55, app: 0.45, deceptive: 0.12 }
    },

    /* ─── NOSTALGIC — Eb minor, music-box language, aeolian cadences ─── */
    nostalgic: {
        root: 63,
        scale: [0, 2, 3, 5, 7, 8, 10],
        melodyCenter: 75,
        tonicDeg: 0, domDeg: 6, subDeg: 5,
        lhBase: 0.26, rhBase: 0.55,
        progs: [
            { name: 'box',      bars: [C(0), C(5), C(2), C(6)],           cons: [C(0), C(5), C(6), C(0)],            cad: 'aeol' },
            { name: 'swing',    bars: [C(0,{add9:1}), C(5), C(0,{add9:1}), C(6)], cons: [C(3,{ext:'7'}), C(5), C(6), C(0)], cad: 'aeol' },
            { name: 'locket',   bars: [C(0), C(3,{ext:'7'}), C(0), C(6)], cons: [C(5), C(3), C(6), C(0,{add9:1})],   cad: 'aeol' },
            { name: 'ribbon',   bars: [C(0), C(6), C(5), C(3,{ext:'7'})], cons: [C(0), C(5), C(6), C(0)],            cad: 'aeol' }
        ],
        progsAlt: [
            { name: 'faded',    bars: [C(5), C(6), C(0), C(0)],           cons: [C(5), C(3), C(6), C(0)],            cad: 'aeol' },
            { name: 'keepsake', bars: [C(5), C(0,{add9:1}), C(3,{ext:'7'}), C(6)], cons: [C(5), C(3), C(6), C(0,{add9:1})], cad: 'aeol' }
        ],
        rhy: {
            open: [[1,1,1,1],[2,1,1],[1,1,2],[2,2]],
            mid:  [[1,1,1,1],[0.5,0.5,1,1,1],[2,1,1],[1,1,1,-1],[1,0.5,0.5,1,1],[2,2]],
            cad:  [[2,2],[1,1,2],[4],[2,-1,1]],
            sparse: [[4],[2,-2],[2,1,-1],[1,-1,1,-1]]
        },
        figs: {
            arp8: [
                [{t:'B',o:0,b:0,d:1},{t:2,o:0,b:1,d:1},{t:0,o:1,b:2,d:1},{t:2,o:0,b:3,d:1}],
                [{t:'B',o:0,b:0,d:1.5},{t:2,o:0,b:1.5,d:.5},{t:0,o:1,b:2,d:1},{t:1,o:1,b:3,d:1}],
                [{t:'B',o:0,b:0,d:1},{t:2,o:0,b:1,d:1},{t:1,o:1,b:2,d:1},{t:0,o:1,b:3,d:1}]
            ],
            half: [
                [{t:'B',o:0,b:0,d:2},{t:2,o:0,b:2,d:2}],
                [{t:'B',o:0,b:0,d:2},{t:0,o:1,b:2,d:2}]
            ]
        },
        sections: [
            { name: 'intro',  tex: 'line',    dyn: 0.40, bars: 4,  melody: true },
            { name: 'A',      tex: 'arp8',    dyn: 0.50, bars: 16, melody: true },
            { name: "A'",     tex: 'arp8',    dyn: 0.60, bars: 16, melody: true, dyads: true },
            { name: 'bridge', tex: 'chorale', dyn: 0.38, bars: 8,  melody: false },
            { name: 'B',      tex: 'arp8',    dyn: 0.55, bars: 16, melody: true, alt: true },
            { name: "A''",    tex: 'arp8',    dyn: 0.64, bars: 16, melody: true, dyads: true },
            { name: 'breath', tex: 'chorale', dyn: 0.34, bars: 4,  melody: false }
        ],
        dev: { susp: 0.35, app: 0.30, deceptive: 0.08 }
    },

    /* ─── ZEN — C# major pentatonic, koto/spa language ───
       No semitones in the scale → no leading-tone tension by construction.
       Degree stacking over 5 notes yields open quartal voicings (D#-G#-C#)
       and a cadential I6-4 on degree 3 (G#-C#-E#) instead of triads. Long
       ringing tones, sparse rhythm, quiet dynamics. NOTE: pentatonic gaps
       are 3 semitones — needs the k<=3 scaleStep above. */
    zen: {
        root: 61,
        scale: [0, 2, 4, 7, 9],
        melodyCenter: 73,
        tonicDeg: 0, domDeg: 3, subDeg: 4,
        lhBase: 0.26, rhBase: 0.52,
        progs: [
            { name: 'stone',  bars: [C(0), C(1), C(4), C(3)], cons: [C(0), C(1), C(3), C(0)], cad: 'auth' },
            { name: 'water',  bars: [C(0), C(4), C(1), C(0)], cons: [C(2), C(1), C(3), C(0)], cad: 'auth' },
            { name: 'bamboo', bars: [C(1), C(0), C(2), C(1)], cons: [C(4), C(2), C(1), C(0)], cad: 'plagal' },
            { name: 'reed',   bars: [C(0), C(2), C(4), C(3)], cons: [C(0), C(4), C(3), C(0)], cad: 'auth' }
        ],
        progsAlt: [
            { name: 'moon',   bars: [C(4), C(3), C(0), C(1)], cons: [C(4), C(1), C(3), C(0,{add9:1})], cad: 'auth' },
            { name: 'ripple', bars: [C(3), C(1), C(4), C(0)], cons: [C(2), C(3), C(1), C(0,{add9:1})], cad: 'plagal' }
        ],
        rhy: {
            /* The THEME generator needs >=3 onsets per cell (makeTheme), and
               only [1,1,2] of the original opening cells qualified — the rest
               fell through to `mid`, leaving zen just THREE possible theme
               rhythms. Measured effect: 8 of 60 New-Melody presses reproduced
               an earlier tune note-for-note (every other mood: 60/60 unique,
               probe 2026-07-29). These six added cells all carry a breath
               (rest) or a long ringing tone, so the ma-and-space character is
               preserved while the theme space roughly triples. */
            open: [[4],[2,2],[3,1],[2,-1,1],[1,1,2],
                   [1,1,-1,1],[2,-0.5,0.5,1],[1,-0.5,1.5,1],
                   [0.5,1.5,-1,1],[1,1,1,1],[1.5,0.5,-1,1]],
            mid:  [[2,2],[1,1,2],[2,1,1],[3,1],[1,-1,2],[2,1,-1],[1,2,1]],
            cad:  [[4],[2,2],[3,1],[1,3]],
            sparse: [[4],[2,-2],[-1,2,-1],[3,-1]]
        },
        figs: {
            arp8: [
                [{t:'B',o:0,b:0,d:2},{t:1,o:0,b:2,d:1},{t:2,o:0,b:3,d:1}],
                [{t:'B',o:0,b:0,d:1},{t:1,o:0,b:1,d:1},{t:2,o:0,b:2,d:1},{t:1,o:1,b:3,d:1}],
                [{t:'B',o:0,b:0,d:1.5},{t:2,o:0,b:1.5,d:.5},{t:1,o:0,b:2,d:2}]
            ],
            half: [
                [{t:'B',o:0,b:0,d:4}],
                [{t:'B',o:0,b:0,d:2},{t:2,o:0,b:2,d:2}],
                [{t:'B',o:0,b:0,d:3},{t:1,o:0,b:3,d:1}]
            ]
        },
        /* jo-ha-kyū pacing: slow entry (jo), gradual quickening (ha: A'→B),
           brief peak (kyū: A''), release. Melody architecture comes from the
           dedicated zen period planner (statement→echo→ma), not from the
           classical antecedent/consequent walk — see startPeriodZen. */
        /* hook-first: no intro section — the theme statement (with its kakite
           octave accent) IS bar 1. Attention-economy finding: hit intros have
           collapsed to ~5 s (Léveillé Gauvin 2017); the ma bars breathe inside
           the phrase architecture, the piece must not idle before it. */
        sections: [
            { name: 'A',      tex: 'drone',   dyn: 0.44, bars: 16, melody: true,  tempoMul: 1.04 },
            { name: "A'",     tex: 'drone',   dyn: 0.50, bars: 16, melody: true,  tempoMul: 1.00 },
            { name: 'bridge', tex: 'chorale', dyn: 0.36, bars: 8,  melody: false, tempoMul: 1.03 },
            { name: 'B',      tex: 'drone',   dyn: 0.55, bars: 16, melody: true,  alt: true, tempoMul: 0.96 },
            { name: "A''",    tex: 'drone',   dyn: 0.60, bars: 16, melody: true,  tempoMul: 0.93 },
            { name: 'breath', tex: 'chorale', dyn: 0.30, bars: 4,  melody: false, tempoMul: 1.10 }
        ],
        dev: { susp: 0.10, app: 0.20, deceptive: 0.10 },
        style: { theme: 1, zen: 1 }
    },

    /* ─── FLIGHT — G lydian, film-wonder language ───
       The lydian signature is the diatonic II MAJOR (A-C#-E over G) and the
       #4 (C#) it carries; progressions plane I↔II. Degree 3 (C#-E-G, dim)
       is deliberately never used as a chord root. Wide flowing arpeggios,
       brighter dynamics than dreamy. */
    flight: {
        root: 55,
        scale: [0, 2, 4, 6, 7, 9, 11],
        melodyCenter: 71,
        tonicDeg: 0, domDeg: 4, subDeg: 5,
        lhBase: 0.30, rhBase: 0.62,
        progs: [
            { name: 'soar',    bars: [C(0,{add9:1}), C(1), C(0,{add9:1}), C(4)], cons: [C(0,{add9:1}), C(1), C(4,{sus4:1}), C(0,{add9:1})], cad: 'auth' },
            { name: 'shimmer', bars: [C(0,{ext:'7'}), C(1,{ext:'7'}), C(2,{ext:'7'}), C(1)], cons: [C(0,{ext:'7'}), C(1), C(4), C(0,{add9:1})], cad: 'auth' },
            { name: 'hymn',    bars: [C(0), C(6), C(1), C(4)], cons: [C(5), C(1), C(4), C(0)], cad: 'auth' },
            { name: 'glide',   bars: [C(0,{add9:1}), C(1), C(4), C(1)], cons: [C(0,{add9:1}), C(1), C(4,{sus4:1}), C(0,{add9:1})], cad: 'auth' }
        ],
        progsAlt: [
            { name: 'wings',   bars: [C(1), C(0), C(1), C(2)], cons: [C(1), C(5), C(4,{sus4:1}), C(0,{add9:1})], cad: 'auth' },
            { name: 'updraft', bars: [C(1), C(0,{add9:1}), C(6), C(4)], cons: [C(5), C(1), C(4), C(0,{add9:1})], cad: 'auth' }
        ],
        rhy: {
            open: [[2,1,1],[1,1,2],[1.5,0.5,2],[2,2],[1,0.5,0.5,2]],
            mid:  [[1,1,1,1],[1.5,0.5,1,1],[0.5,0.5,1,1,1],[2,1,1],[1,1,1.5,0.5],[1,0.5,0.5,1,1]],
            cad:  [[2,2],[1,3],[1.5,0.5,2]],
            sparse: [[4],[2,2],[3,-1]]
        },
        figs: {
            arp8: [
                [{t:'B',o:0,b:0,d:.5},{t:2,o:0,b:.5,d:.5},{t:0,o:1,b:1,d:.5},{t:1,o:1,b:1.5,d:.5},{t:2,o:1,b:2,d:.5},{t:1,o:1,b:2.5,d:.5},{t:0,o:1,b:3,d:.5},{t:2,o:0,b:3.5,d:.5}],
                [{t:'B',o:0,b:0,d:.5},{t:1,o:0,b:.5,d:.5},{t:2,o:0,b:1,d:.5},{t:0,o:1,b:1.5,d:.5},{t:1,o:1,b:2,d:.5},{t:0,o:1,b:2.5,d:.5},{t:2,o:0,b:3,d:.5},{t:1,o:0,b:3.5,d:.5}],
                [{t:'B',o:0,b:0,d:1},{t:2,o:0,b:1,d:.5},{t:0,o:1,b:1.5,d:.5},{t:1,o:1,b:2,d:1},{t:2,o:0,b:3,d:1}]
            ],
            half: [
                [{t:'B',o:0,b:0,d:2},{t:2,o:0,b:2,d:1},{t:1,o:1,b:3,d:1}],
                [{t:'B',o:0,b:0,d:2},{t:1,o:1,b:2,d:2}]
            ]
        },
        /* hook-first: the soaring theme opens at second 0 — no idle intro */
        sections: [
            { name: 'A',      tex: 'arp8',    dyn: 0.58, bars: 16, melody: true },
            { name: "A'",     tex: 'arp8',    dyn: 0.72, bars: 16, melody: true, dyads: true },
            { name: 'bridge', tex: 'half',    dyn: 0.45, bars: 8,  melody: true },
            { name: 'B',      tex: 'arp8',    dyn: 0.64, bars: 16, melody: true, alt: true },
            /* climax rides a tonic PEDAL POINT (film practice: I/II plane over
               a held bass) and the rising "rule of three" sequence */
            { name: "A''",    tex: 'arp8',    dyn: 0.76, bars: 16, melody: true, dyads: true, pedal: true },
            { name: 'breath', tex: 'chorale', dyn: 0.36, bars: 4,  melody: false }
        ],
        dev: { susp: 0.40, app: 0.45, deceptive: 0.12 },
        style: { theme: 1, risingSeq: 1, pedalPoint: 1 }
    }
};

/* ============================== */
/* COMPOSER v2                    */
/* ============================== */
function createComposer(mood, bpm, octaves, seed) {
    var M = MOODS[mood] || MOODS.calm;
    var rng = mulberry32(hashStr(mood + '-' + bpm + '-' + seed));

    var melodySpan = 10 + octaves * 3;
    var winLo = M.melodyCenter - Math.floor(melodySpan / 2);
    var winHi = M.melodyCenter + Math.ceil(melodySpan / 2) + 4;

    var c = {
        mood: mood, bpm: bpm, octaves: octaves,
        harmonyScale: { root: M.root, notes: M.scale },
        winLo: winLo, winHi: winHi,
        barIndex: 0,
        sectionIdx: 0, sectionBarsLeft: M.sections[0].bars,
        periodPos: 0,
        curCad: 'auth',
        antRhythms: null,
        antOpenGuide: null,
        plan: [],
        planChords: [],
        lastMelPitch: M.melodyCenter,
        lastMelWasGuide: true,
        prevBass: M.root - 24,
        prevVoicing: null,
        /* v3 (themed moods only): the performance's theme + section loop +
           verbatim-repeat cache */
        theme: null,
        secProg: null, secProgSection: -1,
        periodCache: null, periodCacheSection: -1, periodRepeat: 0,
        rng: rng
    };

    function section() { return M.sections[c.sectionIdx]; }
    function pick(arr) { return arr[Math.floor(rng() * arr.length)]; }

    function ctCandidates(spec, lo, hi) {
        var core = chordCore(M.scale, spec), out = [];
        for (var i = 0; i < core.length; i++) {
            var base = M.root + core[i];
            var m = foldToRange(base, lo, lo + 11);
            for (; m <= hi; m += 12) if (m >= lo) out.push({ midi: m, tone: i });
        }
        out.sort(function (a, b) { return a.midi - b.midi; });
        return out;
    }
    function isChordTone(midi, spec) {
        var core = chordCore(M.scale, spec);
        for (var i = 0; i < core.length; i++) if (pcOf(midi) === pcOf(M.root + core[i])) return true;
        return false;
    }
    function inScale(midi, spec) {
        var pc = pcOf(midi - M.root);
        for (var i = 0; i < M.scale.length; i++) if (pcOf(M.scale[i]) === pc) return true;
        /* the raised third of a raise3 chord (leading tone over V) is legal */
        if (spec && spec.raise3) {
            return pc === pcOf(degToSemisIn(M.scale, spec.d + 2) + 1);
        }
        return false;
    }
    function scaleStep(midi, dir, spec) {
        /* probe up to 3 semitones: pentatonic scales (zen) have 3-semitone
           gaps — the old k<=2 walked off-scale there; 7-note scales always
           hit within 2, so their behavior is untouched */
        for (var k = 1; k <= 3; k++) {
            var m = midi + dir * k;
            if (inScale(m, spec)) return m;
        }
        return midi + dir * 2;
    }
    function nearestCT(midi, spec, lo, hi) {
        var cands = ctCandidates(spec, lo, hi);
        if (!cands.length) return midi;
        var best = cands[0].midi, bd = 1e9;
        for (var i = 0; i < cands.length; i++) {
            var d = Math.abs(cands[i].midi - midi);
            if (d < bd) { bd = d; best = cands[i].midi; }
        }
        return best;
    }

    function pickGuideNear(spec, near, bias) {
        var cands = ctCandidates(spec, winLo, winHi), best = near, bs = -1e9;
        for (var i = 0; i < cands.length; i++) {
            var cd = cands[i];
            /* a static guide makes the phrase drone on one note — hard-avoid
               repeating `near` unless the chord offers nothing else */
            var s = -Math.abs(cd.midi - near)
                    + (bias ? (((cd.midi - near) * bias > 0) ? 0.8 : -0.8) : 0)
                    + (cd.tone === 1 ? 0.9 : cd.tone === 0 ? 0.5 : cd.tone === 2 ? 0.6 : 0.2)
                    + (cd.midi === near ? -3.0 : 0);
            if (s > bs) { bs = s; best = cd.midi; }
        }
        return best;
    }

    function planGuides(chords, goal, openGuide) {
        var g = [];
        if (openGuide != null && isChordTone(openGuide, chords[0])) g[0] = openGuide;
        else {
            var cands = ctCandidates(chords[0], winLo, winHi), best = M.melodyCenter, bs = -1e9;
            for (var i = 0; i < cands.length; i++) {
                var cd = cands[i];
                var s = -Math.abs(cd.midi - c.lastMelPitch) * 0.8
                        - Math.abs(cd.midi - M.melodyCenter) * 0.25
                        + (cd.tone === 1 ? 2.2 : cd.tone === 0 ? 1.2 : cd.tone === 2 ? 1.4 : 0.4);
                if (s > bs) { bs = s; best = cd.midi; }
            }
            g[0] = best;
        }
        g[1] = pickGuideNear(chords[1], g[0], 0);
        var top = Math.max(g[0], g[1]);
        var cl = ctCandidates(chords[2], top + 1, winHi);
        g[2] = cl.length ? cl[0].midi : pickGuideNear(chords[2], top, 1);
        if (goal === 'tonic') {
            var tonicPc = pcOf(M.root + degToSemisIn(M.scale, M.tonicDeg));
            var t = g[2];
            while (pcOf(t) !== tonicPc) t--;
            if (t < winLo) t += 12;
            g[3] = t;
        } else if (goal === 'deceptive') {
            g[3] = pickGuideNear(chords[3], g[2] - 2, -1);
        } else {
            g[3] = pickGuideNear(chords[3], g[2] - 1, -1);
        }
        return g;
    }

    function fillBar(rhythm, gCur, gNext, spec, nextSpec, isCadBar, goal, climaxBar) {
        var ev = [];
        var beat = 0;
        var slots = [];
        for (var i = 0; i < rhythm.length; i++) {
            var d = rhythm[i];
            if (d < 0) { beat += -d; continue; }
            slots.push({ b: beat, d: d });
            beat += d;
        }
        if (!slots.length) return ev;

        var pitches = new Array(slots.length);
        var roles = new Array(slots.length);
        pitches[0] = gCur;
        roles[0] = 'CT';

        /* suspension: hold the previous bar's non-guide tone over this chord,
           resolve DOWN by step (prepared 4-3 / 9-8 feel). Not on short cadence
           bars — the cadence formula owns the last two slots there and would
           overwrite the resolution. */
        var minSlots = isCadBar ? 4 : 2;
        if (slots.length >= minSlots && !c.lastMelWasGuide && rng() < M.dev.susp) {
            var below = scaleStep(c.lastMelPitch, -1, spec);
            if (!isChordTone(c.lastMelPitch, spec) && isChordTone(below, spec) && Math.abs(c.lastMelPitch - gCur) <= 4) {
                pitches[0] = c.lastMelPitch; roles[0] = 'susp';
                pitches[1] = below;          roles[1] = 'res';
                gCur = below;
            }
        }
        /* appoggiatura at the climax: lean from above ON the beat, resolve down */
        if (climaxBar && roles[0] === 'CT' && slots.length >= 2 && rng() < M.dev.app) {
            pitches[0] = scaleStep(gCur, 1, spec); roles[0] = 'app';
            pitches[1] = gCur;                     roles[1] = 'res';
        }

        var startIdx = 0;
        while (startIdx < slots.length && roles[startIdx]) startIdx++;

        for (var k = startIdx; k < slots.length; k++) {
            var remaining = slots.length - k;
            var prev = pitches[k - 1] != null ? pitches[k - 1] : gCur;
            var distSteps = 0, probe = prev, guardUp = 0;
            while (probe < gNext && guardUp < 15) { probe = scaleStep(probe, 1, nextSpec); distSteps++; guardUp++; }
            var guardDn = 0;
            while (probe > gNext && guardDn < 15) { probe = scaleStep(probe, -1, nextSpec); distSteps--; guardDn++; }

            if (distSteps === 0) {
                /* hover: mostly neighbors, else a chord-skip AWAY from prev */
                var move = (rng() < 0.75) ? (rng() < 0.5 ? 1 : -1) : 0;
                var p0;
                if (move === 0) {
                    var dirSkip = rng() < 0.5 ? 1 : -1;
                    p0 = nearestCT(prev + dirSkip * 3, spec, winLo, winHi);
                    if (p0 === prev) p0 = nearestCT(prev - dirSkip * 3, spec, winLo, winHi);
                    if (p0 === prev) p0 = scaleStep(prev, dirSkip, spec);
                } else {
                    p0 = scaleStep(prev, move, spec);
                }
                pitches[k] = p0;
                roles[k] = isChordTone(p0, spec) ? 'CT' : 'nb';
            } else {
                var stepsPerSlot = distSteps / remaining;
                if (Math.abs(stepsPerSlot) > 2.2) {
                    var target = nearestCT(gNext - (distSteps > 0 ? 2 : -2), spec, winLo, winHi);
                    pitches[k] = target;
                    roles[k] = 'CT';
                } else {
                    var mv = Math.round(stepsPerSlot);
                    if (mv === 0) mv = distSteps > 0 ? 1 : -1;
                    if (mv > 2) mv = 2; if (mv < -2) mv = -2;
                    var p1 = prev;
                    for (var s2 = 0; s2 < Math.abs(mv); s2++) p1 = scaleStep(p1, mv > 0 ? 1 : -1, spec);
                    pitches[k] = p1;
                    roles[k] = isChordTone(p1, spec) ? 'CT' : 'pass';
                }
            }
        }

        /* interior strong beat (beat 2) carries a chord tone — the rule, not a coin */
        for (var k2 = 1; k2 < slots.length; k2++) {
            if (slots[k2].b >= 2 && slots[k2].b < 2.5 && (roles[k2] === 'pass' || roles[k2] === 'nb')) {
                pitches[k2] = nearestCT(pitches[k2], spec, winLo, winHi);
                roles[k2] = 'CT';
            }
        }

        /* cadence formula: approach the final by step — 2̂→1̂ or 7̂→1̂, whichever
           neighbor lies CLOSER to the preceding tone (kills approach leaps) */
        if (isCadBar && slots.length >= 2) {
            var fin = gNext;
            var penUp = scaleStep(fin, 1, spec);
            var penDn = scaleStep(fin, -1, spec);
            var before = (slots.length >= 3 && pitches[slots.length - 3] != null)
                ? pitches[slots.length - 3] : (pitches[0] != null ? pitches[0] : fin);
            var pen = (Math.abs(penUp - before) <= Math.abs(penDn - before)) ? penUp : penDn;
            pitches[slots.length - 1] = fin;
            roles[slots.length - 1] = 'cadF';
            pitches[slots.length - 2] = pen;
            roles[slots.length - 2] = 'pen';
        }

        for (var e2 = 0; e2 < slots.length; e2++) {
            if (pitches[e2] == null) continue;
            pitches[e2] = foldToRange(pitches[e2], winLo, winHi);
            /* a fold must not break a resolution: recompute it from the folded
               leaning tone so susp/app still resolve DOWN by step */
            if (roles[e2] === 'res' && e2 > 0) {
                pitches[e2] = foldToRange(scaleStep(pitches[e2 - 1], -1, spec), winLo, winHi);
            }
            ev.push({ startBeat: slots[e2].b, durBeats: slots[e2].d, midi: pitches[e2], role: roles[e2] || 'pass' });
        }
        if (ev.length) {
            c.lastMelPitch = ev[ev.length - 1].midi;
            var lastRole = ev[ev.length - 1].role;
            c.lastMelWasGuide = (lastRole === 'CT' || lastRole === 'cadF' || lastRole === 'res');
        }
        return ev;
    }

    function planPhrase(chords, rhythms, goal, openGuide) {
        var guides = planGuides(chords, goal, openGuide);
        var bars = [];
        for (var i = 0; i < 4; i++) {
            var gNext = (i < 3) ? guides[i + 1] : guides[3];
            bars.push(fillBar(rhythms[i], guides[i], gNext, chords[i], chords[Math.min(i + 1, 3)], i === 3, goal, i === 2));
        }
        /* phrase-level leap recovery — counterpoint's "step back after a leap",
           applied across barlines (protected roles keep their pitches) */
        var flat = [];
        for (var b2 = 0; b2 < 4; b2++) for (var e3 = 0; e3 < bars[b2].length; e3++) flat.push({ bar: b2, ev: bars[b2][e3] });
        var protectedRole = { app: 1, susp: 1, res: 1, cadF: 1, pen: 1 };
        for (var f2 = 1; f2 < flat.length - 1; f2++) {
            var cur = flat[f2].ev, prev2 = flat[f2 - 1].ev, nxt = flat[f2 + 1].ev;
            var leap = cur.midi - prev2.midi;
            if (Math.abs(leap) <= 5) continue;
            var after = nxt.midi - cur.midi;
            if (Math.abs(after) <= 2) continue;               /* leap left by step — idiomatic */
            /* a leap INTO a leaning tone that resolves by step is the textbook
               appoggiatura gesture — leave it be */
            if (nxt.role === 'app' || nxt.role === 'susp' || cur.role === 'app' || cur.role === 'susp') continue;
            var nxtStrong = (nxt.startBeat === 0 || nxt.startBeat === 2);
            if (!protectedRole[nxt.role] && !nxtStrong) {
                /* classic: step back the opposite way after the leap */
                var spec2 = chords[flat[f2 + 1].bar];
                var back2 = scaleStep(cur.midi, leap > 0 ? -1 : 1, spec2);
                nxt.midi = foldToRange(back2, winLo, winHi);
                nxt.role = isChordTone(nxt.midi, spec2) ? 'CT' : 'pass';
            } else if (!protectedRole[cur.role] && !(cur.startBeat === 0 || cur.startBeat === 2)) {
                /* target is fixed (guide/cadence) — soften the leap at its source */
                var specC = chords[flat[f2].bar];
                cur.midi = foldToRange(scaleStep(prev2.midi, leap > 0 ? 1 : -1, specC), winLo, winHi);
                cur.role = isChordTone(cur.midi, specC) ? 'CT' : 'pass';
            } else if (!protectedRole[prev2.role] && !(prev2.startBeat === 0 || prev2.startBeat === 2)) {
                /* both ends fixed — bridge from the leap's origin: the weak note
                   before the landing steps toward it instead of away */
                var specP = chords[flat[f2 - 1].bar];
                prev2.midi = foldToRange(scaleStep(cur.midi, leap > 0 ? -1 : 1, specP), winLo, winHi);
                prev2.role = isChordTone(prev2.midi, specP) ? 'CT' : 'pass';
            }
        }
        /* strong beats carry chord tones — re-assert for unclassified tones */
        for (var f3 = 0; f3 < flat.length; f3++) {
            var it = flat[f3].ev;
            if ((it.startBeat === 0 || it.startBeat === 2) && (it.role === 'pass' || it.role === 'nb')) {
                var spec3 = chords[flat[f3].bar];
                it.midi = nearestCT(it.midi, spec3, winLo, winHi);
                it.role = 'CT';
            }
        }
        return { bars: bars, guides: guides };
    }

    function startPeriod() {
        var sec = section();
        var pool = (sec.alt && M.progsAlt && M.progsAlt.length) ? M.progsAlt : M.progs;
        var prog = pick(pool);
        var chords = prog.bars.concat(prog.cons);
        var goal = 'tonic';
        c.curCad = prog.cad;
        if (prog.cad === 'auth' && rng() < M.dev.deceptive) {
            goal = 'deceptive';
            chords = chords.slice();
            chords[7] = C(M.subDeg, { add9: rng() < 0.4 });
            c.curCad = 'deceptive';
        }
        c.planChords = chords;
        var sparse = (sec.tex === 'line' || sec.dyn < 0.45);
        var rOpen = pick(sparse ? M.rhy.sparse.concat(M.rhy.open) : M.rhy.open);
        var rCad = pick(M.rhy.cad);
        var rMid1 = pick(M.rhy.mid), rMid2 = pick(M.rhy.mid);
        c.antRhythms = [rOpen, rMid1, rMid2, rCad];

        var ant = planPhrase(chords.slice(0, 4), c.antRhythms, 'half', null);
        c.antOpenGuide = ant.guides[0];
        var cons = planPhrase(chords.slice(4, 8), c.antRhythms, goal, c.antOpenGuide);
        c.plan = ant.bars.concat(cons.bars);
    }

    /* ============================== */
    /* COMPOSER v3 — thematic memory  */
    /* (opt-in via MOODS[m].style —   */
    /* the four v2 moods never enter  */
    /* these paths)                   */
    /* ============================== */

    /*
     * THE THEME: one motif per performance (Einaudi economy: a piece is 2-3
     * cells varied, not fresh material every phrase). Rhythm comes from the
     * mood's opening vocabulary; contour is a single-peak arch in scale steps.
     */
    function countOnsets(rhythm) {
        var n = 0;
        for (var i = 0; i < rhythm.length; i++) if (rhythm[i] > 0) n++;
        return n;
    }
    function makeTheme() {
        var rhythm = pick(M.rhy.open), guard = 0;
        while (countOnsets(rhythm) < 3 && guard++ < 8) rhythm = pick(M.rhy.mid);
        var n = countOnsets(rhythm);
        var peakAt = 1 + Math.floor(rng() * Math.max(1, n - 2));
        var contour = [];
        for (var i = 1; i < n; i++) {
            contour.push(i <= peakAt ? (rng() < 0.7 ? 1 : 2)
                                     : (rng() < 0.75 ? -1 : -2));
        }
        return { rhythm: rhythm, contour: contour };
    }

    /* realize the theme in one bar over a chord: start at the chord tone
       nearest the guide (shifted for sequences), walk the contour by scale
       steps, snap strong beats to chord tones */
    function realizeThemeBar(theme, spec, anchor, stepShift, invert) {
        var slots = [], beat = 0, i;
        for (i = 0; i < theme.rhythm.length; i++) {
            var d = theme.rhythm[i];
            if (d < 0) { beat += -d; continue; }
            slots.push({ b: beat, d: d });
            beat += d;
        }
        var p = nearestCT(anchor, spec, winLo, winHi);
        if (stepShift) for (i = 0; i < Math.abs(stepShift); i++) p = scaleStep(p, stepShift > 0 ? 1 : -1, spec);
        var evs = [];
        for (var k = 0; k < slots.length; k++) {
            if (k > 0) {
                var delta = theme.contour[k - 1] * (invert ? -1 : 1);
                for (var q = 0; q < Math.abs(delta); q++) p = scaleStep(p, delta > 0 ? 1 : -1, spec);
            }
            var midi = (slots[k].b === 0 || slots[k].b === 2) ? nearestCT(p, spec, winLo, winHi) : p;
            midi = foldToRange(midi, winLo, winHi);
            evs.push({ startBeat: slots[k].b, durBeats: slots[k].d, midi: midi, role: 'thm' });
            p = midi;
        }
        if (evs.length) { c.lastMelPitch = evs[evs.length - 1].midi; c.lastMelWasGuide = true; }
        return evs;
    }

    /* Einaudi harmonic discipline: ONE progression loops per section */
    function sectionProg() {
        if (c.secProgSection !== c.sectionIdx || !c.secProg) {
            var sec = section();
            var pool = (sec.alt && M.progsAlt && M.progsAlt.length) ? M.progsAlt : M.progs;
            c.secProg = pick(pool);
            c.secProgSection = c.sectionIdx;
        }
        return c.secProg;
    }

    /* Margulis discipline: a freshly built period is repeated VERBATIM once
       before new material may appear — literal repetition is the strongest
       "composed by a human" signal listeners have */
    function tryRepeatCache() {
        if (c.periodCacheSection === c.sectionIdx && c.periodCache && c.periodRepeat < 1) {
            c.periodRepeat++;
            c.plan = c.periodCache.plan;
            c.planChords = c.periodCache.chords;
            c.curCad = c.periodCache.cad;
            return true;
        }
        return false;
    }
    function storeCache() {
        c.periodCache = { plan: c.plan, chords: c.planChords, cad: c.curCad };
        c.periodCacheSection = c.sectionIdx;
        c.periodRepeat = 0;
    }

    function pickTransform(sec) {
        if (sec.alt) return { shift: 0, invert: true };   /* B contrasts by inversion */
        var r = rng();
        if (r < 0.45) return { shift: 0, invert: false };
        if (r < 0.70) return { shift: 1, invert: false };
        if (r < 0.90) return { shift: -1, invert: false };
        return { shift: 0, invert: true };
    }

    /* themed period: theme statement heads each phrase, v2 machinery supplies
       the continuation and the cadence (classical sentence anatomy) */
    function startPeriodV3() {
        var sec = section();
        if (!c.theme) c.theme = makeTheme();
        if (tryRepeatCache()) return;

        var prog = sectionProg();
        var chords = prog.bars.concat(prog.cons);
        var goal = 'tonic';
        c.curCad = prog.cad;
        if (prog.cad === 'auth' && rng() < M.dev.deceptive) {
            goal = 'deceptive';
            chords = chords.slice();
            chords[7] = C(M.subDeg, { add9: rng() < 0.4 });
            c.curCad = 'deceptive';
        }
        c.planChords = chords;

        var rCad = pick(M.rhy.cad), rMid1 = pick(M.rhy.mid), rMid2 = pick(M.rhy.mid);
        c.antRhythms = [c.theme.rhythm, rMid1, rMid2, rCad];
        var ant = planPhrase(chords.slice(0, 4), c.antRhythms, 'half', null);
        c.antOpenGuide = ant.guides[0];
        var cons = planPhrase(chords.slice(4, 8), c.antRhythms, goal, c.antOpenGuide);

        var tf = pickTransform(sec);
        ant.bars[0] = realizeThemeBar(c.theme, chords[0], ant.guides[0], 0, false);
        if (M.style.risingSeq && sec.dyn >= 0.7) {
            /* the "rule of three": statement, +1 step, +2 steps, then cadence */
            cons.bars[0] = realizeThemeBar(c.theme, chords[4], ant.guides[0], 0, false);
            cons.bars[1] = realizeThemeBar(c.theme, chords[5], ant.guides[0], 1, false);
            cons.bars[2] = realizeThemeBar(c.theme, chords[6], ant.guides[0], 2, false);
        } else {
            cons.bars[0] = realizeThemeBar(c.theme, chords[4], ant.guides[0], tf.shift, tf.invert);
        }
        c.plan = ant.bars.concat(cons.bars);
        storeCache();
    }

    /* ── zen period: statement → echo → ma (間) → answer → descent → ma ──
       No antecedent/consequent walk: the yo scale has no leading tones, so
       meaning is carried by breath, register and heterophonic echo instead
       of dissonance mechanics (Koizumi nuclear tones; shakuhachi breath). */
    function zenTail(spec) {
        var a = scaleStep(c.lastMelPitch, -1, spec);
        var b = nearestCT(scaleStep(a, -1, spec), spec, winLo, winHi);
        c.lastMelPitch = b;
        return [
            { startBeat: 0.5, durBeats: 1.5, midi: foldToRange(a, winLo, winHi), role: 'pass' },
            { startBeat: 2,   durBeats: 2,   midi: foldToRange(b, winLo, winHi), role: 'CT' }
        ];
    }
    function zenCadence(spec) {
        /* rest on a NUCLEAR tone (root or fifth), approached from the step
           above, final held long — finality is temporal, not harmonic */
        var rootPc = pcOf(M.root + degToSemisIn(M.scale, M.tonicDeg));
        var fifthPc = pcOf(M.root + degToSemisIn(M.scale, 3));
        var t = c.lastMelPitch, guard = 0;
        while (pcOf(t) !== rootPc && pcOf(t) !== fifthPc && guard++ < 24) t--;
        t = foldToRange(t, winLo, winLo + 11);   /* settle LOW-mid register */
        var pre = scaleStep(t, 1, spec);
        c.lastMelPitch = t;
        return [
            { startBeat: 0, durBeats: 1, midi: foldToRange(pre, winLo, winHi), role: 'pen' },
            { startBeat: 1, durBeats: 3, midi: t, role: 'cadF' }
        ];
    }
    function startPeriodZen() {
        var sec = section();
        if (!c.theme) c.theme = makeTheme();
        if (tryRepeatCache()) return;

        var prog = sectionProg();
        var chords = prog.bars.concat(prog.cons);
        c.curCad = prog.cad;
        c.planChords = chords;
        var plan = [[], [], [], [], [], [], [], []];

        /* Opening register varies per performance: the very first statement
           starts from a different tone of the tonic chord (nearestCT snaps the
           offset onto a real chord tone). Every later period inherits its
           anchor from the line itself. Without this the whole 8-bar zen period
           was determined by (theme rhythm x contour x progression) alone — a
           small space that produced audible repeats across New Melody presses;
           this multiplies it by the register choice. Starting a koto-style
           piece high or low is idiomatic, so the character is untouched. */
        var anchor = c.lastMelPitch;
        if (!c.zenOpened) {
            c.zenOpened = 1;
            anchor = M.melodyCenter + [-7, -3, 0, 2, 5][Math.floor(rng() * 5)];
        }
        plan[0] = realizeThemeBar(c.theme, chords[0], anchor, 0, false);
        /* kakite accent: the statement's first note doubled an octave below */
        if (plan[0].length && rng() < 0.5) {
            var h = plan[0][0];
            plan[0].unshift({ startBeat: h.startBeat, durBeats: h.durBeats, midi: foldToRange(h.midi - 12, winLo - 12, winHi), role: 'dy8' });
        }
        plan[1] = zenTail(chords[1]);
        /* heterophonic echo: the tail returns an octave up, softer, after a
           breath (shizugaki octave-repeat practice). The breath length varies —
           a fixed gap made every zen period breathe identically. */
        var echoGap = rng() < 0.6 ? 1 : 2;
        plan[2] = plan[1].map(function (e) {
            return { startBeat: Math.min(3, e.startBeat + echoGap), durBeats: e.durBeats,
                     midi: foldToRange(e.midi + 12, winLo, winHi), role: 'echo' };
        });
        /* plan[3] = MA: melody silent, the drone rings through the gap */
        /* the answer usually restates the theme one scale step lower; a second
           step down, or a step up, are equally idiomatic answers */
        var ansShift = [-1, -1, -2, 1][Math.floor(rng() * 4)];
        plan[4] = realizeThemeBar(c.theme, chords[4], anchor, ansShift, false);
        plan[5] = zenTail(chords[5]);
        plan[6] = zenCadence(chords[6]);
        /* plan[7] = MA — the longest breath, before the next thought */
        c.plan = plan;
        storeCache();
    }

    function bassFor(spec) {
        var core = chordCore(M.scale, spec);
        var prefs = [core[0], core[2], core[1]];
        var best = null, bs = 1e9;
        for (var i = 0; i < prefs.length; i++) {
            var m = foldToRange(M.root + prefs[i] - 24, 38, 52);
            var cost = Math.abs(m - c.prevBass) + i * 2.5;
            if (cost < bs) { bs = cost; best = m; }
        }
        c.prevBass = best;
        return best;
    }
    function toneMidi(spec, t, bass, o) {
        var core = chordCore(M.scale, spec);
        if (t === 'B') return bass + o * 12;
        if (t === 3 && core.length < 4) t = 2;
        var semis = (t === 9) ? chordColor9(M.scale, spec) : core[t];
        var m = M.root + semis - 12;
        m = foldToRange(m, bass + 1, bass + 14);
        return m + o * 12;
    }
    function accompForBar(spec, sec) {
        var out = [];
        var dynL = M.lhBase * (0.75 + sec.dyn * 0.5);
        var pedal = sec.pedal && M.style && M.style.pedalPoint;

        /* zen drone: open fifth on the chord root, ringing through the bar —
           "space, not silence": the resonance carries the ma. Rolled octave
           (koto sweep) marks phrase entries. */
        if (sec.tex === 'drone') {
            var dRoot = foldToRange(M.root + degToSemisIn(M.scale, spec.d) - 12, 38, 52);
            var dTop = inScale(dRoot + 7, spec) ? dRoot + 7 : (inScale(dRoot + 5, spec) ? dRoot + 5 : dRoot + 12);
            c.prevBass = dRoot;
            out.push({ startBeat: 0, durBeats: 4, midi: dRoot, vel: Math.min(0.4, dynL * 1.0), sus: 1, hand: 'L' });
            out.push({ startBeat: 0, durBeats: 4, midi: dTop, vel: Math.min(0.4, dynL * 0.7), sus: 1, hand: 'L' });
            if (c.periodPos === 0 || c.periodPos === 4) {
                out.push({ startBeat: 0.12, durBeats: 3.8, midi: dRoot + 12, vel: Math.min(0.4, dynL * 0.55), sus: 1, hand: 'L' });
            }
            return out;
        }

        if (sec.tex === 'chorale') {
            var bass = pedal ? (c.prevBass = foldToRange(M.root + degToSemisIn(M.scale, M.tonicDeg) - 12, 38, 50)) : bassFor(spec);
            var core = chordCore(M.scale, spec);
            var want = [core[1], core[2], spec.add9 ? chordColor9(M.scale, spec) : core[0]];
            var voices = [];
            if (c.prevVoicing) {
                for (var v = 0; v < 3; v++) {
                    var bestM = null, bd = 1e9;
                    for (var w = 0; w < want.length; w++) {
                        var targetPc = pcOf(M.root + want[w]);
                        for (var oct = -1; oct <= 1; oct++) {
                            var mm = c.prevVoicing[v] + oct * 12;
                            mm = mm + (targetPc - pcOf(mm) + 18) % 12 - 6;   /* nearest same-pc */
                            mm = foldToRange(mm, 55, 79);
                            var d0 = Math.abs(mm - c.prevVoicing[v]);
                            if (d0 < bd && voices.indexOf(mm) === -1 && pcOf(mm) === targetPc) { bd = d0; bestM = mm; }
                        }
                    }
                    voices.push(bestM != null ? bestM : foldToRange(M.root + want[v], 55, 79));
                }
            } else {
                voices = [
                    foldToRange(M.root + want[0], 55, 66),
                    foldToRange(M.root + want[1], 60, 71),
                    foldToRange(M.root + want[2], 66, 79)
                ];
            }
            c.prevVoicing = voices;
            out.push({ startBeat: 0, durBeats: 4, midi: bass, vel: Math.min(0.4, dynL * 1.1), sus: 1, hand: 'L' });
            for (var v2 = 0; v2 < 3; v2++) {
                out.push({ startBeat: 0, durBeats: 4, midi: voices[v2], vel: Math.min(0.4, dynL * 0.8), sus: 1, hand: 'L' });
            }
            return out;
        }
        var figs = M.figs[sec.tex] || M.figs.arp8;
        var fig = figs[Math.floor(rng() * figs.length)];
        /* film pedal point: the bass holds the tonic while chords plane above */
        var bass2 = pedal ? (c.prevBass = foldToRange(M.root + degToSemisIn(M.scale, M.tonicDeg) - 12, 38, 50)) : bassFor(spec);
        for (var i2 = 0; i2 < fig.length; i2++) {
            var f = fig[i2];
            var m2 = toneMidi(spec, f.t, bass2, f.o);
            var vel = dynL * (f.t === 'B' ? 1.15 : 0.85) * (0.92 + rng() * 0.16);
            out.push({ startBeat: f.b, durBeats: f.d, midi: m2, vel: Math.min(0.4, vel), sus: 1, hand: 'L' });
            if (f.dy === 2) {
                out.push({ startBeat: f.b, durBeats: f.d, midi: toneMidi(spec, 2, bass2, f.o), vel: Math.min(0.4, vel * 0.9), sus: 1, hand: 'L' });
            }
        }
        return out;
    }

    function dyadBelow(midi, spec) {
        var deg = null;
        for (var d = -14; d < 40; d++) {
            if (pcOf(M.root + degToSemisIn(M.scale, d)) === pcOf(midi)) { deg = d; break; }
        }
        if (deg == null) return null;
        var third = M.root + degToSemisIn(M.scale, deg - 2);
        third = third + 12 * Math.round((midi - 4 - third) / 12);
        if (midi - third > 9 || midi - third < 2) {
            var sixth = M.root + degToSemisIn(M.scale, deg - 5);
            sixth = sixth + 12 * Math.round((midi - 8 - sixth) / 12);
            return sixth;
        }
        return third;
    }

    c.nextBar = function () {
        var sec = section();
        if (c.periodPos === 0) {
            if (M.style && M.style.zen) startPeriodZen();
            else if (M.style && M.style.theme) startPeriodV3();
            else startPeriod();
        }

        var spec = c.planChords[c.periodPos];
        var melody = [];
        if (sec.melody) {
            var barEvents = c.plan[c.periodPos] || [];
            var arc = [0.82, 0.9, 1.0, 0.86][c.periodPos % 4];
            var consSoft = c.periodPos >= 4 ? 0.96 : 1.0;
            for (var i = 0; i < barEvents.length; i++) {
                var evn = barEvents[i];
                var vel = M.rhBase * (sec.dyn / 0.6) * arc * consSoft * (0.9 + rng() * 0.2);
                if (evn.role === 'app' || evn.role === 'susp') vel *= 1.10;
                if (evn.role === 'res') vel *= 0.88;
                if (evn.role === 'cadF') vel *= 0.92;
                /* v3 roles (themed moods only — old moods never emit these) */
                if (evn.role === 'echo') vel *= 0.55;
                if (evn.role === 'dy8') vel *= 0.7;
                vel = Math.max(0.08, Math.min(0.9, vel));
                melody.push({ startBeat: evn.startBeat, durBeats: evn.durBeats, midi: evn.midi, vel: vel, role: evn.role, hand: 'R' });
                if (sec.dyads && evn.durBeats >= 1 && evn.role !== 'app' && rng() < 0.8) {
                    var dm = dyadBelow(evn.midi, spec);
                    if (dm && dm > winLo - 8) {
                        melody.push({ startBeat: evn.startBeat, durBeats: evn.durBeats, midi: dm, vel: Math.max(0.06, vel * 0.72), role: 'dy', hand: 'R' });
                    }
                }
            }
        }
        var accomp = (sec.tex === 'line') ? [] : accompForBar(spec, sec);

        var rit = 1.0;
        if (c.periodPos === 3) rit = 1.03;
        if (c.periodPos === 6) rit = 1.02;
        if (c.periodPos === 7) rit = 1.06;
        if (c.sectionBarsLeft === 1) rit *= 1.05;
        /* jo-ha-kyū: zen sections carry their own tempo curve (slow entry,
           gradual quickening, release) */
        if (sec.tempoMul && M.style && M.style.zen) rit *= sec.tempoMul;
        /* luftpause: themed moods breathe before each new period */
        if (c.periodPos === 0 && M.style && M.style.theme) rit *= 1.02;

        var res = {
            melody: melody, accomp: accomp, ritMul: rit,
            chordSpec: spec, section: sec.name, texture: sec.tex, cad: c.curCad,
            periodPos: c.periodPos
        };

        c.barIndex++;
        c.periodPos = (c.periodPos + 1) % 8;
        c.sectionBarsLeft--;
        if (c.sectionBarsLeft <= 0) {
            c.sectionIdx = (c.sectionIdx + 1) % M.sections.length;
            /* an INTRO plays once; hook-first moods (no intro section) cycle
               back through their opening statement */
            if (c.sectionIdx === 0 && M.sections[0].name === 'intro') c.sectionIdx = 1;
            c.sectionBarsLeft = M.sections[c.sectionIdx].bars;
            c.periodPos = 0;
            c.prevVoicing = null;
        }
        return res;
    };

    return c;
}

/* ============================== */
/* SAMPLER (browser only)         */
/* ============================== */
var SAMPLE_NOTES = [
    { name: 'C2', midi: 36 }, { name: 'Ds2', midi: 39 }, { name: 'Fs2', midi: 42 }, { name: 'A2', midi: 45 },
    { name: 'C3', midi: 48 }, { name: 'Ds3', midi: 51 }, { name: 'Fs3', midi: 54 }, { name: 'A3', midi: 57 },
    { name: 'C4', midi: 60 }, { name: 'Ds4', midi: 63 }, { name: 'Fs4', midi: 66 }, { name: 'A4', midi: 69 },
    { name: 'C5', midi: 72 }, { name: 'Ds5', midi: 75 }, { name: 'Fs5', midi: 78 }, { name: 'A5', midi: 81 },
    { name: 'C6', midi: 84 }, { name: 'Ds6', midi: 87 }, { name: 'Fs6', midi: 90 }, { name: 'A6', midi: 93 },
    { name: 'C7', midi: 96 }
];

function createSampler(ctx, outBus, opts) {
    opts = opts || {};
    var s = {
        buffers: {},
        loadedCount: 0,
        failedCount: 0,
        active: [],
        loading: false,
        onProgress: opts.onProgress || function () {}
    };

    /* chunked: the plain full-buffer loop was a burst of 100-500 ms main-
       thread tasks (21 files) ~15-25 s after Play on weak devices — the
       "почему тормозит" long-task source found by the 2026-07-17 perf probe.
       Total work is unchanged; it just yields between 256k-sample slices. */
    function downmixMono(buf) {
        if (buf.numberOfChannels === 1) return Promise.resolve(buf);
        var mono = ctx.createBuffer(1, buf.length, buf.sampleRate);
        var out = mono.getChannelData(0);
        var l = buf.getChannelData(0), r = buf.getChannelData(1);
        var CHUNK = 262144;
        return new Promise(function (resolve) {
            var i = 0;
            function slice() {
                var end = Math.min(buf.length, i + CHUNK);
                for (; i < end; i++) out[i] = (l[i] + r[i]) * 0.5;
                if (i < buf.length) setTimeout(slice, 0);
                else resolve(mono);
            }
            slice();
        });
    }

    s.load = function (baseUrl, version) {
        if (s.loading) return;
        s.loading = true;
        var order = SAMPLE_NOTES.slice().sort(function (a, b) {
            return Math.abs(a.midi - 66) - Math.abs(b.midi - 66);
        });
        var idx = 0, inflight = 0, PAR = 3;
        function next() {
            while (inflight < PAR && idx < order.length) {
                (function (note) {
                    inflight++;
                    fetch(baseUrl + '/' + note.name + '.mp3' + (version ? '?v=' + version : ''))
                        .then(function (r) { if (!r.ok) throw new Error('http ' + r.status); return r.arrayBuffer(); })
                        .then(function (ab) {
                            return new Promise(function (res, rej) { ctx.decodeAudioData(ab, res, rej); });
                        })
                        .then(function (buf) { return downmixMono(buf); })
                        .then(function (monoBuf) {
                            s.buffers[note.midi] = monoBuf;
                            s.loadedCount++;
                            s.onProgress(s.loadedCount, SAMPLE_NOTES.length);
                        })
                        .catch(function () { s.failedCount++; })
                        .then(function () { inflight--; next(); });
                })(order[idx++]);
            }
        }
        next();
    };

    function anchorFor(midi) {
        var best = null, bestDist = 2.01;
        for (var i = 0; i < SAMPLE_NOTES.length; i++) {
            var m = SAMPLE_NOTES[i].midi;
            if (!s.buffers[m]) continue;
            var d = Math.abs(m - midi);
            if (d < bestDist) { bestDist = d; best = m; }
        }
        return best;
    }

    s.ready = function (midi) { return anchorFor(midi) !== null; };

    s.noteOn = function (midi, vel, when, durSec) {
        var anchor = anchorFor(midi);
        if (anchor === null) return false;
        var buf = s.buffers[anchor];
        var src = ctx.createBufferSource();
        src.buffer = buf;
        src.playbackRate.value = Math.pow(2, (midi - anchor) / 12);

        var gain = ctx.createGain();
        var level = 0.9 * Math.pow(vel, 1.35);
        gain.gain.setValueAtTime(0, when);
        gain.gain.linearRampToValueAtTime(level, when + 0.004);
        var relStart = when + Math.max(durSec, 0.15);
        var relDur = 1.4;
        gain.gain.setValueAtTime(level, relStart);
        gain.gain.exponentialRampToValueAtTime(0.0001, relStart + relDur);

        var lpf = ctx.createBiquadFilter();
        lpf.type = 'lowpass';
        lpf.frequency.value = 1800 + vel * 8200;
        lpf.Q.value = 0.4;

        var pan = ctx.createStereoPanner ? ctx.createStereoPanner() : ctx.createGain();
        if (pan.pan) pan.pan.value = Math.max(-0.6, Math.min(0.6, (midi - 60) / 38));

        src.connect(lpf); lpf.connect(gain); gain.connect(pan); pan.connect(outBus);
        var stopAt = Math.min(relStart + relDur + 0.1, when + buf.duration / src.playbackRate.value + 0.1);
        src.start(when);
        try { src.stop(stopAt); } catch (e) {}

        var rec = { src: src, gain: gain, lpf: lpf, pan: pan, stopAt: stopAt };
        s.active.push(rec);
        src.onended = function () {
            var i = s.active.indexOf(rec);
            if (i !== -1) s.active.splice(i, 1);
            try { src.disconnect(); gain.disconnect(); lpf.disconnect(); pan.disconnect(); } catch (e) {}
        };
        return true;
    };

    s.stopAll = function (fadeSec) {
        var now = ctx.currentTime;
        var list = s.active.slice();
        for (var i = 0; i < list.length; i++) {
            var a = list[i];
            try {
                a.gain.gain.cancelScheduledValues(now);
                a.gain.gain.setValueAtTime(Math.max(a.gain.gain.value, 0.0001), now);
                a.gain.gain.exponentialRampToValueAtTime(0.0001, now + fadeSec);
                a.src.stop(now + fadeSec + 0.05);
            } catch (e) {}
        }
        s.active = [];
    };

    return s;
}

/* ============================== */
/* ENGINE (browser only)          */
/* ============================== */
function createEngine(opts) {
    var synth = opts.synth;
    var MOOD_ORDER = ['calm', 'zen', 'dreamy', 'flight', 'melancholic', 'nostalgic'];
    var e = {
        playing: false,
        composer: null,
        sampler: null,
        mood: 'calm', bpm: 60, octaves: 3,
        vizNotes: [],
        startedAt: 0,
        sampleNotesPlayed: 0, synthNotesPlayed: 0,
        clock: null,
        /* auto mood rotation: after `intervalSec` the NEXT period boundary
           (i.e. right after a cadence) starts the next mood — the musical
           thought finishes, the new one begins. No fade needed: in the
           scheduled timeline the old period ends before the new bar starts. */
        rotate: { intervalSec: 0, deadline: 0 },
        _nextBarTime: 0, _gen: 0,
        _upgradeSent: false
    };

    var LOOKAHEAD_VISIBLE = 5, LOOKAHEAD_HIDDEN = 90, TICK_MS = 250;
    var ACCOMP_LAG = 0.015;           /* the tune leads, the left hand follows */

    function ensureSampler() {
        if (e.sampler || !opts.sampleBase) return;
        if (!synth.getInputBus || !synth.getInputBus()) return;
        /* real samples enter past the synth-only body EQ + chorus (the chorus
           put an audible slow pitch-wobble on Salamander recordings) */
        var sampleBus = synth.getSampleBus ? synth.getSampleBus() : synth.getInputBus();
        e.sampler = createSampler(synth.ctx, sampleBus, {
            onProgress: function (n, total) {
                if (!e._upgradeSent && n >= 3) {
                    e._upgradeSent = true;
                    if (opts.onEngineUpgrade) opts.onEngineUpgrade();
                }
                if (n === total && e.sampler.failedCount > 0 && opts.onSampleError) {
                    opts.onSampleError(e.sampler.failedCount);
                }
            }
        });
        e.sampler.load(opts.sampleBase, opts.sampleVersion);
    }

    function scheduleNote(midi, vel, when, durSec) {
        var used = false;
        if (e.sampler && e.sampler.ready(midi)) {
            used = e.sampler.noteOn(midi, vel, when, durSec);
            if (used) e.sampleNotesPlayed++;
        }
        if (!used) {
            synth.noteOn(midi, vel, when);
            synth.noteOff(midi, when + durSec);
            e.synthNotesPlayed++;
        }
        var info = { midi: midi, start: when, end: when + durSec, vel: vel };
        e.vizNotes.push(info);
        if (opts.onNote) opts.onNote(info);
    }

    function tick() {
        if (!e.playing) return;
        var ctx = synth.ctx;
        var ahead = (typeof document !== 'undefined' && document.hidden) ? LOOKAHEAD_HIDDEN : LOOKAHEAD_VISIBLE;
        var horizon = ctx.currentTime + ahead;
        var gen = e._gen;
        while (e._nextBarTime < horizon) {
            if (gen !== e._gen || !e.playing) return;
            /* auto rotation: the interval has elapsed IN THE SCHEDULED TIMELINE
               and the upcoming bar opens a new period (the previous one just
               cadenced) → hand over to the next mood's composer, gapless */
            if (e.rotate.intervalSec > 0 && e.composer.periodPos === 0 && e._nextBarTime >= e.rotate.deadline) {
                var nextMood = MOOD_ORDER[(MOOD_ORDER.indexOf(e.mood) + 1) % MOOD_ORDER.length];
                e.mood = nextMood;
                e.composer = createComposer(nextMood, e.bpm, e.octaves, Date.now());
                e.rotate.deadline = e._nextBarTime + e.rotate.intervalSec;
                if (opts.onRotate) opts.onRotate(nextMood, e._nextBarTime);
            }
            var bar = e.composer.nextBar();
            var beatDur = (60 / e.bpm) * bar.ritMul;
            var barT = e._nextBarTime;
            var barSec = 4 * beatDur;
            var i, m, when, dur;
            for (i = 0; i < bar.melody.length; i++) {
                m = bar.melody[i];
                when = barT + m.startBeat * beatDur + (e.composer.rng() - 0.5) * 0.016;
                dur = m.durBeats * beatDur * 1.35;   /* the tune sings past its written value */
                scheduleNote(m.midi, m.vel, Math.max(when, ctx.currentTime + 0.02), dur);
            }
            for (i = 0; i < bar.accomp.length; i++) {
                m = bar.accomp[i];
                when = barT + m.startBeat * beatDur + ACCOMP_LAG + (e.composer.rng() - 0.5) * 0.01;
                /* sustain pedal: LH rings until the chord changes */
                dur = m.sus ? (barSec - m.startBeat * beatDur) + 0.25 * beatDur
                            : m.durBeats * beatDur;
                scheduleNote(m.midi, m.vel, Math.max(when, ctx.currentTime + 0.02), dur);
            }
            e._nextBarTime += barSec;
        }
        var cutoff = ctx.currentTime - 2;
        e.vizNotes = e.vizNotes.filter(function (x) { return x.end > cutoff; });
    }

    /* enable/disable auto mood rotation; safe to call any time.
       remainSec (optional): resume a PAUSED countdown instead of restarting
       the full interval — stop/play must not reset the user's rotation clock
       (Farid 2026-07-17: 15-min rotation "never fired" across pauses). */
    e.setRotation = function (intervalSec, remainSec) {
        e.rotate.intervalSec = intervalSec || 0;
        e.rotate.pendingRemain = (remainSec != null && remainSec > 0) ? remainSec : null;
        if (synth.ctx) {
            var lead = e.rotate.pendingRemain != null ? e.rotate.pendingRemain : (intervalSec || 0);
            e.rotate.deadline = Math.max(e._nextBarTime, synth.ctx.currentTime) + lead;
        }
    };

    e.start = function (mood, bpm, octaves) {
        e.mood = mood; e.bpm = bpm; e.octaves = octaves;
        e.composer = createComposer(mood, bpm, octaves, Date.now());
        ensureSampler();
        e.playing = true;
        e._gen++;
        e.startedAt = synth.ctx.currentTime;
        e._nextBarTime = synth.ctx.currentTime + 0.25;
        if (e.rotate.intervalSec > 0) {
            e.rotate.deadline = e._nextBarTime +
                (e.rotate.pendingRemain != null ? e.rotate.pendingRemain : e.rotate.intervalSec);
            e.rotate.pendingRemain = null;
        }
        tick();
        if (e.clock) e.clock.stop();
        /* Worker clock — immune to background-tab timer throttling */
        if (typeof window !== 'undefined' && window.__audioBackground) {
            e.clock = window.__audioBackground.createClock(tick, TICK_MS);
        } else {
            var id = setInterval(tick, TICK_MS);
            e.clock = { stop: function () { clearInterval(id); } };
        }
    };

    e.stop = function (graceful) {
        e.playing = false;
        e._gen++;
        if (e.clock) { e.clock.stop(); e.clock = null; }
        if (e.sampler) e.sampler.stopAll(graceful ? 1.0 : 0.2);
        synth.stopAll(!graceful);
        e.vizNotes = [];
        return synth.ctx ? synth.ctx.currentTime - e.startedAt : 0;
    };

    e.change = function (mood, bpm, octaves) {
        var was = e.playing;
        if (was) e.stop(true);
        if (was) e.start(mood, bpm, octaves);
        else { e.mood = mood; e.bpm = bpm; e.octaves = octaves; }
    };

    e.engineKind = function () {
        if (!e.sampler || e.sampler.loadedCount === 0) return 'synth';
        return e.sampler.loadedCount >= SAMPLE_NOTES.length ? 'samples' : 'mixed';
    };

    return e;
}

/* ============================== */
/* OFFLINE TIMELINE (export)      */
/* ============================== */
/*
 * Pure bar-by-bar unroll of the composer for export: the SAME scheduling
 * math as the live tick() — melody sings past its written value (×1.35),
 * the left hand lags the tune by ACCOMP_LAG, pedal notes ring until the
 * chord changes, humanize jitter comes from the composer's own PRNG —
 * but mapped onto an absolute t=0 timeline instead of ctx.currentTime.
 * Each event carries BOTH domains: beats (startBeat/durBeats, for MIDI)
 * and seconds (when/dur, for the offline audio render).
 * No WebAudio — Node-importable, deterministic for a given seed.
 */
function buildTimeline(mood, bpm, octaves, seed, targetSec) {
    var composer = createComposer(mood, bpm, octaves, seed);
    var ACCOMP_LAG = 0.015;
    var bars = [];
    var t = 0;
    while (t < targetSec) {
        var bar = composer.nextBar();
        var beatDur = (60 / bpm) * bar.ritMul;
        var barSec = 4 * beatDur;
        var events = [];
        var i, m;
        for (i = 0; i < bar.melody.length; i++) {
            m = bar.melody[i];
            events.push({
                midi: m.midi, vel: m.vel, hand: 'R',
                startBeat: m.startBeat, durBeats: m.durBeats,
                when: Math.max(0, t + m.startBeat * beatDur + (composer.rng() - 0.5) * 0.016),
                dur: m.durBeats * beatDur * 1.35
            });
        }
        for (i = 0; i < bar.accomp.length; i++) {
            m = bar.accomp[i];
            events.push({
                midi: m.midi, vel: m.vel, hand: 'L',
                startBeat: m.startBeat,
                durBeats: m.sus ? (4 - m.startBeat) + 0.25 : m.durBeats,
                when: Math.max(0, t + m.startBeat * beatDur + ACCOMP_LAG + (composer.rng() - 0.5) * 0.01),
                dur: m.sus ? (barSec - m.startBeat * beatDur) + 0.25 * beatDur
                           : m.durBeats * beatDur
            });
        }
        bars.push({ start: t, beatDur: beatDur, ritMul: bar.ritMul, section: bar.section, events: events });
        t += barSec;
    }
    return { bars: bars, totalSec: t, mood: mood, bpm: bpm, octaves: octaves, seed: seed };
}

var api = {
    createComposer: createComposer,
    createEngine: createEngine,
    createSampler: createSampler,
    buildTimeline: buildTimeline,
    MOODS: MOODS,
    SCALES: {   /* harmony scales per mood (shape kept for existing probes) */
        calm:        { root: 60, notes: MOODS.calm.scale },
        dreamy:      { root: 61, notes: MOODS.dreamy.scale },
        melancholic: { root: 57, notes: MOODS.melancholic.scale },
        nostalgic:   { root: 63, notes: MOODS.nostalgic.scale },
        zen:         { root: 61, notes: MOODS.zen.scale },
        flight:      { root: 55, notes: MOODS.flight.scale }
    },
    SAMPLE_NOTES: SAMPLE_NOTES,
    diatonicChord: diatonicChord,
    chordCore: chordCore,
    degToSemis: degToSemisIn
};

if (typeof module !== 'undefined' && module.exports) module.exports = api;
else root.EndlessPianoEngine = api;

})(typeof window !== 'undefined' ? window : globalThis);
